import React, { useCallback, useId, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { FigmaGlyph } from '../bot-constructor/FigmaGlyph';
import { useTranslation } from 'react-i18next';

type Lang = 'ru' | 'en';

const LANGS: Lang[] = ['ru', 'en'];

const LanguageSelector: React.FC<{ inverted?: boolean; variant?: 'default' | 'editor' }> = ({
  inverted = false,
  variant = 'default',
}) => {
  const { i18n, t } = useTranslation();
  const listId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const detected = i18n.resolvedLanguage || i18n.language || 'ru';
  const currentLanguage: Lang = detected.split('-')[0] === 'en' ? 'en' : 'ru';
  const menuBg = inverted ? '#000000' : '#eceff4';
  const optBorder = inverted ? '#fff' : 'rgba(13,13,13,0.35)';
  const optBorderMuted = inverted ? 'rgba(255,255,255,0.45)' : 'rgba(13,13,13,0.22)';

  const close = useCallback(() => setAnchorEl(null), []);
  const setLang = useCallback((lang: Lang) => {
    if (lang !== currentLanguage) void i18n.changeLanguage(lang);
    close();
  }, [currentLanguage, i18n, close]);

  return (
    <>
      <Box
        component="button"
        type="button"
        id="language-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={t('common.language', 'Язык')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: variant === 'editor' ? '5px' : 0,
          minWidth: variant === 'editor' ? 54 : 40,
          height: variant === 'editor' ? 35 : 40,
          px: variant === 'editor' ? '6px' : 1,
          py: variant === 'editor' ? 0 : 0.5,
          boxSizing: 'border-box',
          borderRadius: variant === 'editor' ? '7px' : '50%',
          cursor: 'pointer',
          m: 0,
          color: variant === 'editor' ? '#ffb300' : inverted ? '#fff' : 'inherit',
          bgcolor: 'transparent',
          border: variant === 'editor' ? '1px solid #d1d1d1' : 'none',
          fontSize: variant === 'editor' ? 13 : 13,
          fontWeight: variant === 'editor' ? 500 : 600,
          lineHeight: variant === 'editor' ? '20px' : 1,
          '&:hover': { bgcolor: inverted ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' },
          '&:focus-visible': {
            outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
            outlineOffset: 2,
          },
        }}
      >
        {currentLanguage.toUpperCase()}
        {variant === 'editor' && (
          <FigmaGlyph src="/images/editor/icon-chevron.svg" width={18} height={18} transform="rotate(-90deg)" />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        marginThreshold={8}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              borderRadius: 1,
              overflow: 'hidden',
              border: inverted ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
              mt: 0.5,
              bgcolor: menuBg,
            },
          },
        }}
      >
        <Box
          id={listId}
          role="listbox"
          aria-labelledby="language-selector-trigger"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1,
            minWidth: 52,
            boxSizing: 'border-box',
          }}
        >
          {LANGS.map((lang) => {
            const selected = lang === currentLanguage;
            return (
              <Box
                key={lang}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={lang === 'ru' ? t('common.languageRu') : t('common.languageEn')}
                onClick={() => setLang(lang)}
                sx={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  p: 0,
                  m: 0,
                  border: `2px solid ${selected ? optBorder : optBorderMuted}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  color: inverted ? '#fff' : 'inherit',
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: selected ? 1 : inverted ? 0.55 : 0.65,
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                  '&:hover': { opacity: 1, transform: 'scale(1.06)' },
                  '&:focus-visible': {
                    outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
                    outlineOffset: 2,
                  },
                }}
              >
                {lang.toUpperCase()}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default LanguageSelector;
